import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clock, AlertTriangle, XCircle, RefreshCw, ExternalLink, Trash2, Send } from 'lucide-react';
import Nango from '@nangohq/frontend';
import { Card } from './MlmShell';

const API = process.env.REACT_APP_BACKEND_URL;

const NETWORKS = [
  {
    id: 'whatsapp', label: 'WhatsApp Business', icon: '💬', nango: 'whatsapp-business',
    bubble: '💡 Pulsa Conectar y autoriza con tu Facebook. Necesitas un número que no esté en uso en otra app de WhatsApp.',
    color: '#25D366',
  },
  {
    id: 'telegram', label: 'Telegram', icon: '✈️', nango: null,
    bubble: '💡 Crea un bot gratis con @BotFather en Telegram (1 minuto) y pega aquí el token que te da.',
    color: '#0088CC',
  },
  {
    id: 'instagram', label: 'Instagram + Facebook', icon: '📸', nango: null,
    bubble: '💡 Tu Instagram debe ser Profesional (Creador o Empresa) y estar vinculado a una Página de Facebook.',
    color: '#E1306C',
  },
  {
    id: 'tiktok', label: 'TikTok', icon: '🎵', nango: null,
    bubble: '💡 Conecta con tu cuenta TikTok. Los posts se publican como borradores privados hasta que TikTok apruebe la app de Neuralia369.',
    color: '#010101',
  },
  {
    id: 'gmail', label: 'Gmail', icon: '✉️', nango: 'google-mail',
    bubble: '💡 Pulsa Conectar y autoriza el acceso a tu Gmail. Podrás leer y enviar correos en tu nombre.',
    color: '#EA4335',
  },
  {
    id: 'email', label: 'Email (Resend)', icon: '📧', nango: null, builtin: true,
    bubble: '✅ Canal de email operativo y configurado. No requiere conexión adicional.',
    color: '#6366F1',
  },
];

const statusConfig = {
  connected: { label: 'Conectado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle2 },
  pending: { label: 'Pendiente', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  error: { label: 'Error', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
  disconnected: { label: 'No conectado', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: XCircle },
};

export const MlmConnections = ({ api, token }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState({});
  const [telegramWizard, setTelegramWizard] = useState(false);
  const [telegramToken, setTelegramToken] = useState('');
  const [telegramStep, setTelegramStep] = useState(1);
  const [telegramError, setTelegramError] = useState('');

  // authFetch directo usando el token del panel MLM
  const directFetch = useCallback(async (url, opts = {}) => {
    const res = await fetch(url, {
      ...opts,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...(opts.headers || {}) },
    });
    return res;
  }, [token]);

  const DIRECT_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const load = useCallback(async () => {
    const res = await api('/connections/status', {}, { silent: true, key: 'conn-status' });
    setStatus(res);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const setLoad = (id, val) => setLoading(l => ({ ...l, [id]: val }));

  // ── Nango connect ──────────────────────────────────────────────────────────
  // Uses the Nango SDK's Connect UI (iframe). The Connect UI reports success as
  // { type: 'connect', payload: { connectionId, providerConfigKey } }; a standalone
  // popup never delivers those events, so the connection was never saved.
  const connectNango = async (net) => {
    setLoad(net.id, true);
    try {
      const res = await api('/connections/nango/session', { method: 'POST', body: JSON.stringify({ integration: net.nango }) }, { silent: true, key: `conn-nango-${net.id}` });
      if (!res?.session_token) throw new Error('No se pudo iniciar sesión Nango');
      const nango = new Nango();
      const connectUi = nango.openConnectUI({
        lang: 'es',
        onEvent: async (evt) => {
          if (evt.type === 'connect') {
            const { connectionId, providerConfigKey } = evt.payload || {};
            if (connectionId && providerConfigKey) {
              try {
                await api('/connections/nango/callback', {
                  method: 'POST',
                  body: JSON.stringify({ integration: providerConfigKey, connection_id: connectionId }),
                }, { silent: true, key: `conn-nango-cb-${net.id}` });
              } catch (e) { console.warn('Nango callback error:', e); }
            }
            await load();
          } else if (evt.type === 'close' || evt.type === 'error') {
            await load();
            setLoad(net.id, false);
          }
        },
      });
      connectUi.setSessionToken(res.session_token);
    } catch (e) {
      console.error(e);
      setLoad(net.id, false);
    }
  };

  // ── Telegram wizard ────────────────────────────────────────────────────────
  const connectTelegram = async () => {
    if (!telegramToken.trim()) { setTelegramError('Pega el token de @BotFather'); return; }
    setLoad('telegram', true); setTelegramError('');
    const res = await api('/connections/telegram/connect', { method: 'POST', body: JSON.stringify({ bot_token: telegramToken }) }, { silent: true, key: 'conn-tg' });
    setLoad('telegram', false);
    if (res?.success) { setTelegramWizard(false); setTelegramToken(''); setTelegramStep(1); load(); }
    else setTelegramError(res?.detail || 'Token no válido. Revisa que lo copiaste completo desde @BotFather.');
  };

  // ── Meta OAuth ─────────────────────────────────────────────────────────────
  const connectMeta = async () => {
    setLoad('instagram', true);
    try {
      const res = await directFetch(`${DIRECT_API}/social/meta/oauth/start?owner=self`);
      const data = await res.json();
      if (!res.ok || !data.auth_url) { setLoad('instagram', false); return; }
      const popup = window.open(data.auth_url, 'meta_auth', 'width=520,height=680,left=200,top=100');
      const onMsg = (e) => {
        if (e.data?.type === 'META_OAUTH_SUCCESS') {
          window.removeEventListener('message', onMsg);
          setLoad('instagram', false);
          setTimeout(load, 800);
        }
      };
      window.addEventListener('message', onMsg);
      const poll = setInterval(() => {
        if (!popup || popup.closed) { clearInterval(poll); window.removeEventListener('message', onMsg); setLoad('instagram', false); setTimeout(load, 600); }
      }, 1200);
    } catch (e) {
      console.error('Meta OAuth error:', e);
      setLoad('instagram', false);
    }
  };

  // ── TikTok OAuth ───────────────────────────────────────────────────────────
  const connectTikTok = async () => {
    setLoad('tiktok', true);
    try {
      const res = await directFetch(`${DIRECT_API}/social/tiktok/oauth/start?owner=self`);
      const data = await res.json();
      if (data?.auth_url) window.open(data.auth_url, 'tiktok_auth', 'width=520,height=680,left=200,top=100');
    } catch (e) { console.error('TikTok OAuth error:', e); }
    setLoad('tiktok', false);
  };

  // ── Disconnect ─────────────────────────────────────────────────────────────
  const disconnect = async (net) => {
    if (!window.confirm(`¿Desconectar ${net.label}?`)) return;
    setLoad(net.id, true);
    if (net.id === 'telegram') await api('/connections/telegram/disconnect', { method: 'DELETE' }, { success: 'Telegram desconectado', key: 'conn-tg-disc', silent: true });
    else if (net.nango) await api(`/connections/nango/${net.id}`, { method: 'DELETE' }, { success: `${net.label} desconectado`, key: `conn-disc-${net.id}`, silent: true });
    await load(); setLoad(net.id, false);
  };

  const handleConnect = (net) => {
    if (net.builtin) return;
    if (net.nango) connectNango(net);
    else if (net.id === 'telegram') { setTelegramWizard(true); setTelegramStep(1); }
    else if (net.id === 'instagram') connectMeta();
    else if (net.id === 'tiktok') connectTikTok();
  };

  const getNetStatus = (net) => {
    if (net.builtin) return 'connected';
    const s = (status || {})[net.id];
    return (s?.status) || 'disconnected';
  };

  const connected = NETWORKS.filter(n => getNetStatus(n) === 'connected').length;

  return (
    <div className="space-y-6" data-testid="mlm-connections">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-[#062A5E] to-[#0B3F8C] p-6 text-white">
        <p className="text-xs font-bold uppercase tracking-[3px] opacity-70">Tus redes · Tu cuenta</p>
        <h2 className="mt-1 text-2xl font-black">Conexiones</h2>
        <p className="mt-1 text-sm opacity-80">Cada red se conecta con la cuenta personal del cliente. Neuralia369 y sus clientes trabajan con cuentas separadas — nada se mezcla.</p>
        {/* Progress */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold">{connected} de {NETWORKS.length} herramientas conectadas</span>
            <span className="opacity-70">{Math.round((connected / NETWORKS.length) * 100)}%</span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/20">
            <div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 transition-all duration-700"
              style={{ width: `${(connected / NETWORKS.length) * 100}%` }} />
          </div>
        </div>
      </div>

      {/* Grid de tarjetas */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="mlm-connections-grid">
        {NETWORKS.map(net => {
          const st = getNetStatus(net);
          const cfg = statusConfig[st] || statusConfig.disconnected;
          const StatusIcon = cfg.icon;
          const conn = (status || {})[net.id] || {};
          const busy = loading[net.id];

          return (
            <div key={net.id}
              className="group relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-white/10 bg-[#0a1628] p-5 transition-all hover:border-white/20 hover:shadow-lg"
              data-testid={`mlm-conn-card-${net.id}`}>
              {/* Top */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{net.icon}</span>
                  <div>
                    <p className="font-bold text-white">{net.label}</p>
                    {conn.bot_username && <p className="text-xs text-slate-400">@{conn.bot_username}</p>}
                    {conn.connected_at && <p className="text-xs text-slate-500">Conectado {conn.connected_at?.slice(0, 10)}</p>}
                  </div>
                </div>
                <span className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${cfg.color}`}>
                  <StatusIcon className="h-3 w-3" />{cfg.label}
                </span>
              </div>

              {/* TikTok App Review badge */}
              {net.id === 'tiktok' && (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-xs text-amber-400">
                  ⏳ Publicación en modo borrador — revisión de TikTok en curso. Los posts quedan privados hasta aprobación.
                </div>
              )}

              {/* Bubble */}
              <div className="rounded-xl bg-[#0B3F8C]/30 px-3 py-2.5 text-xs leading-relaxed text-slate-300">
                {net.bubble}
              </div>

              {/* Actions */}
              {st === 'connected' ? (
                <div className="flex flex-col gap-2">
                  {conn.last_sync && (
                    <p className="text-xs text-slate-500">Última sync: {conn.last_sync?.slice(0, 16).replace('T', ' ')}</p>
                  )}
                  {!net.builtin && (
                    <button onClick={() => disconnect(net)} disabled={busy}
                      className="flex items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm font-semibold text-red-400 transition hover:bg-red-500/20 disabled:opacity-50"
                      data-testid={`mlm-conn-disconnect-${net.id}`}>
                      <Trash2 className="h-4 w-4" />Desconectar
                    </button>
                  )}
                </div>
              ) : net.builtin ? null : (
                <button onClick={() => handleConnect(net)} disabled={busy}
                  className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0B3F8C] to-cyan-600 px-4 py-3 text-sm font-bold text-white shadow transition hover:opacity-90 disabled:opacity-50"
                  data-testid={`mlm-conn-connect-${net.id}`}>
                  {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                  {busy ? 'Conectando…' : `Conectar ${net.label}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Botones inferiores */}
      <div className="flex flex-wrap gap-3">
        <button onClick={load}
          className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
          data-testid="mlm-conn-refresh">
          <RefreshCw className="h-4 w-4" />Actualizar estado
        </button>
        <a href="mailto:soporte@neuralia369.com?subject=Ayuda%20conexiones%20MLM"
          className="flex items-center gap-2 rounded-xl border border-[#0B3F8C]/50 bg-[#0B3F8C]/20 px-5 py-2.5 text-sm font-semibold text-[#60a5fa] transition hover:bg-[#0B3F8C]/30"
          data-testid="mlm-conn-help">
          <Send className="h-4 w-4" />Solicitar ayuda
        </a>
      </div>

      {/* Wizard Telegram */}
      {telegramWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="mlm-telegram-wizard">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a1628] p-6 shadow-2xl">
            <h3 className="text-lg font-black text-white">✈️ Conectar Telegram</h3>
            <p className="mt-1 text-sm text-slate-400">Paso {telegramStep} de 3</p>

            {telegramStep === 1 && (
              <div className="mt-4 space-y-3">
                <div className="rounded-xl bg-[#0B3F8C]/30 p-4 text-sm text-slate-300 leading-relaxed">
                  <p className="font-bold text-white mb-2">Cómo crear tu bot en Telegram:</p>
                  <ol className="space-y-1.5 list-decimal list-inside">
                    <li>Abre Telegram en tu móvil</li>
                    <li>Busca <strong className="text-cyan-400">@BotFather</strong> y ábrelo</li>
                    <li>Escribe <code className="bg-white/10 px-1 rounded">/newbot</code></li>
                    <li>Elige un nombre para tu bot (ej: "Neuralia Laura Bot")</li>
                    <li>Elige un username terminado en <code className="bg-white/10 px-1 rounded">_bot</code></li>
                    <li>BotFather te dará un <strong className="text-emerald-400">token</strong> — cópialo</li>
                  </ol>
                </div>
                <button onClick={() => setTelegramStep(2)}
                  className="w-full rounded-xl bg-gradient-to-r from-[#0088CC] to-cyan-500 py-3 text-sm font-bold text-white">
                  Ya tengo el token →
                </button>
              </div>
            )}

            {telegramStep === 2 && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-slate-300">Pega el token que te dio @BotFather:</p>
                <input
                  type="text" value={telegramToken} onChange={e => { setTelegramToken(e.target.value); setTelegramError(''); }}
                  placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                  data-testid="mlm-telegram-token-input"
                />
                {telegramError && <p className="text-xs text-red-400">{telegramError}</p>}
                <div className="flex gap-2">
                  <button onClick={() => setTelegramStep(1)}
                    className="flex-1 rounded-xl border border-white/10 py-2.5 text-sm text-slate-400 hover:bg-white/5">← Atrás</button>
                  <button onClick={connectTelegram} disabled={loading.telegram}
                    className="flex-1 rounded-xl bg-[#0088CC] py-2.5 text-sm font-bold text-white disabled:opacity-50"
                    data-testid="mlm-telegram-connect-btn">
                    {loading.telegram ? 'Validando…' : 'Conectar bot'}
                  </button>
                </div>
              </div>
            )}

            <button onClick={() => { setTelegramWizard(false); setTelegramStep(1); setTelegramToken(''); setTelegramError(''); }}
              className="mt-3 w-full text-xs text-slate-500 hover:text-slate-400">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};
